import { vec3 } from 'gl-matrix'
import GLArrayBuffer from 'nanogl/arraybuffer'
import GLIndexBuffer from 'nanogl/indexbuffer'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import GLState, { LocalConfig } from 'nanogl-state/GLState'
import Vao from 'nanogl-vao'

import { HexToVec3 } from '@/webgl/core/Color'
import { RenderContext } from '@/webgl/core/RenderContext'
import RenderMask from '@/webgl/core/RenderMask'
import RenderPass from '@/webgl/core/RenderPass'
import FontAtlas, { TextStyle } from '@/webgl/engine/FontAtlas'
import Programs from '@/webgl/glsl/programs'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'

export default class TextRenderer implements IRenderable {
  node: Node
  font: FontAtlas
  text: string
  style: TextStyle

  vao: Vao
  positions: GLArrayBuffer
  texcoord: GLArrayBuffer
  indices: GLIndexBuffer
  program: Program

  cfg: LocalConfig

  _invalid: boolean = true
  _color: vec3 = vec3.fromValues(1, 1, 1)
  _outlineColor: vec3 = vec3.fromValues(0, 0, 0)

  outline: number = 0.0
  blur: number = 0.0

  cutoutCfg: LocalConfig
  cutout: Program

  constructor(gl: GLContext) {
    this.node = new Node()
    this.positions = new GLArrayBuffer(gl, gl.FLOAT, gl.DYNAMIC_DRAW)
    this.positions.attrib('aPosition', 2, gl.FLOAT)

    this.texcoord = new GLArrayBuffer(gl, gl.FLOAT, gl.DYNAMIC_DRAW)
    this.texcoord.attrib('aTexCoord', 2, gl.FLOAT)

    this.indices = new GLIndexBuffer(gl, gl.UNSIGNED_SHORT, null, gl.DYNAMIC_DRAW)

    this.program = Programs(gl).get('text')

    this.vao = new Vao(gl)
    this.vao.bind()
    this.vao.setup(this.program, [this.positions, this.texcoord], this.indices)
    this.vao.unbind()

    this.cutout = Programs(gl).get('text-cutout')

    this.cfg = GLState.get(gl).config()
    this.cfg.enableBlend(true).enableDepthTest(true).blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA).depthMask(false)

    this.cutoutCfg = GLState.get(gl).config()
    this.cutoutCfg.enableBlend(false).enableDepthTest(true).depthMask(true)
  }

  invalidate() {
    this._invalid = true
  }

  setStyle(style: TextStyle) {
    this.style = style
    if (style.color) HexToVec3(style.color, this._color)
    if (style.outlineColor) HexToVec3(style.outlineColor, this._outlineColor)
    this.outline = style.outline ?? 0.0
    this.invalidate()
  }

  setFont(font: FontAtlas) {
    this.font = font
    this.invalidate()
  }

  setText(text: string) {
    this.text = text
    this.invalidate()
  }

  preRender(dt: number): void {}

  update() {
    if (this._invalid) {
      this._invalid = false
      const data = this.font.createText(this.text, this.style)
      this.positions.data(data.positions)
      this.texcoord.data(data.uvs)
      this.indices.data(data.indices)
    }
  }

  renderCutout(context: RenderContext) {
    this.update()

    this.cutout.use()
    this.cutout.uMVP(context.camera.getMVP(this.node._wmatrix))
    this.cutout.tFontAtlas(this.font.texture)

    this.cutoutCfg.apply()
    this.vao.bind()
    this.indices.drawTriangles()
    this.vao.unbind()
  }

  renderDefault(context: RenderContext) {
    this.update()

    this.program.use()
    this.program.uMVP(context.camera.getMVP(this.node._wmatrix))
    this.program.tFontAtlas(this.font.texture)
    this.program.uColor(this._color)
    this.program.uOutlineColor(this._outlineColor)
    this.program.uOutline?.(1.0 - this.outline)
    this.program.uBlur?.(this.blur)

    this.cfg.apply()
    this.vao.bind()
    this.indices.drawTriangles()
    this.vao.unbind()
  }

  render(context: RenderContext): void {
    if ((context.mask & RenderMask.OPAQUE) !== 0 && context.pass == RenderPass.COLOR_MASK) this.renderCutout(context)

    if ((context.mask & RenderMask.BLENDED) !== 0 && context.pass != RenderPass.COLOR_MASK) this.renderDefault(context)
  }
}
